import { useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import type { FC } from 'react';
import { isTrueMobile } from 'shared/utils/isTrueMobile';
import { idleManager } from 'core/services/managers/IdleManager';
import { halloweenLoaders, christmasLoaders, useBrandingPreload } from './loadBranding';
import { GenericErrorBoundary } from 'shared/components/ErrorBoundaries/GenericErrorBoundary';
import { useSeasonalPalette } from 'shared/hooks/useSeasonalPalette';
import { ROTATION_FREEZE_MS, SNOW_PERFORMANCE_MODE } from '../../../constants';
import { useFeatureFlagsStore, useSeasonalBranding } from 'store/featureFlags/featureFlagsStore';

// Seasonal overlays live in the branding-<name> chunks (see loadBranding.ts). They only render
// once useBrandingPreload reports 'ready', so React.lazy resolves from the installed chunk.
const SnowRain = lazy(christmasLoaders.SnowRain);
const ChristmasIntro = lazy(christmasLoaders.ChristmasIntro);
const BatFlyby = lazy(halloweenLoaders.BatFlyby);
const HalloweenCat = lazy(halloweenLoaders.HalloweenCat);
const HalloweenIntro = lazy(halloweenLoaders.HalloweenIntro);

interface SeasonalOverlaysProps {
  lobbyReady: boolean;
}

const SeasonalOverlays: FC<SeasonalOverlaysProps> = ({ lobbyReady }) => {
  const seasonalBranding = useSeasonalBranding();
  // Preload on the raw flag, not filtered by the player's on/off toggle: switching branding back on
  // in settings then needs no late chunk request (old chunks are deleted on deploy).
  const rawSeasonalBranding = useFeatureFlagsStore((state) => state.flags.seasonalBranding);
  const brandingStatus = useBrandingPreload(rawSeasonalBranding);
  // State for seasonal effect (SnowRain / BatFlyby / HalloweenCat) animation control. The
  // Christmas section dressing itself is static background layers in Section.module.css; only
  // the falling snow is an overlay.
  const [seasonalEffectPaused, setSeasonalEffectPaused] = useState(false);
  // Mirrors the idle manager so the rotation freeze below never un-pauses an idle lobby.
  const isIdleRef = useRef(false);

  useEffect(() => {
    // Register the seasonal effects (snow, lights / bats, cat) for idle management
    idleManager.registerProcess(
      'seasonal-effect',
      () => {
        isIdleRef.current = false;
        setSeasonalEffectPaused(false);
      },
      () => {
        isIdleRef.current = true;
        setSeasonalEffectPaused(true);
      },
    );

    return () => {
      idleManager.unregisterProcess('seasonal-effect');
    };
  }, []);

  // Freeze the seasonal overlays across a rotation: unmount them the moment the orientation flips,
  // bring them back ROTATION_FREEZE_MS after the last flip, unless the lobby went idle meanwhile
  // (then the idle manager owns the resume). The stagger in useSeasonalPalette is keyed off
  // lobbyReady only, so a rotation remounts the overlays without replaying it.
  useEffect(() => {
    const portraitQuery = window.matchMedia('(orientation: portrait)');
    let resumeTimer: number | undefined;

    const handleOrientationChange = (): void => {
      window.clearTimeout(resumeTimer);
      setSeasonalEffectPaused(true);
      resumeTimer = window.setTimeout(() => {
        if (!isIdleRef.current) {
          setSeasonalEffectPaused(false);
        }
      }, ROTATION_FREEZE_MS);
    };

    portraitQuery.addEventListener('change', handleOrientationChange);

    return () => {
      window.clearTimeout(resumeTimer);
      portraitQuery.removeEventListener('change', handleOrientationChange);
    };
  }, []);

  const showChristmasEffect = useMemo(() => seasonalBranding === 'christmas', [seasonalBranding]);
  const showHalloweenEffect = useMemo(() => seasonalBranding === 'halloween', [seasonalBranding]);
  // Stamps the palette attribute on <html>; plays the seasonal intro once the lobby is up and
  // paces the seasonal mounts behind it (see the stage comment on the overlays below).
  // Held back until the branding chunk is installed: useSeasonalPalette stamps any branding it is
  // given, even before lobbyReady, so a loading or failed chunk passes no branding at all (stock
  // lobby, no stamp), and lobbyReady keeps the curtain timeline from starting before the chunk.
  const brandingChunkPending = brandingStatus === 'loading' || brandingStatus === 'failed';
  const { phase: seasonalIntroPhase, stage: seasonalStage } = useSeasonalPalette(
    brandingChunkPending ? undefined : seasonalBranding,
    lobbyReady && brandingStatus === 'ready',
  );
  const showAmbientEffects = seasonalStage === 'ambient' || seasonalStage === 'all';
  const showCat = seasonalStage === 'all';

  // Sampled once per session, not tracked live: one snow sheet on phones (layer memory).
  const snowRainProps = useMemo(
    () => ({
      layers: isTrueMobile() ? (1 as const) : (2 as const),
    }),
    [],
  );

  // Sampled once per session, not tracked live: Fewer bats on phones (memory).
  const batFlybyProps = useMemo(
    () => ({
      batCount: isTrueMobile() ? 3 : SNOW_PERFORMANCE_MODE ? 4 : 6,
    }),
    [],
  );

  return (
    <>
      {/* Seasonal effect overlays (Christmas: a 40vh snow band; Halloween: bats + cat; the
          Christmas section dressing is static section background layers) — unmounted outright
          while idle rather than paused: paused CSS animations still count in
          document.getAnimations(), and unmounting the cat also clears its poll timer and
          releases the video decoder. Staggered behind the lobby so their work stays out of the
          section-mount / thumbnail / jackpot boot window: curtain first, ambient effects at
          3.5 s, the cat at 5 s (1.5 s and 3 s when no curtain plays, e.g. on return from a
          game). */}
      {/* Lazy (branding chunk): each overlay suspends for one tick on its first mount, so each
          gets its own Suspense; a shared one would hide the overlays already up (display: none
          restarts their animations) whenever a later stage mounts. */}
      <GenericErrorBoundary type="general" identifier="seasonal-branding" fallback={() => null}>
        {!seasonalEffectPaused && (
          <>
            {showChristmasEffect && showAmbientEffects && (
              <Suspense fallback={null}>
                <SnowRain {...snowRainProps} />
              </Suspense>
            )}
            {showHalloweenEffect && showAmbientEffects && (
              <Suspense fallback={null}>
                <BatFlyby {...batFlybyProps} />
              </Suspense>
            )}
            {showHalloweenEffect && showCat && (
              <Suspense fallback={null}>
                <HalloweenCat />
              </Suspense>
            )}
          </>
        )}
        {seasonalIntroPhase !== 'idle' && showHalloweenEffect && (
          <Suspense fallback={null}>
            <HalloweenIntro phase={seasonalIntroPhase} />
          </Suspense>
        )}
        {seasonalIntroPhase !== 'idle' && showChristmasEffect && (
          <Suspense fallback={null}>
            <ChristmasIntro phase={seasonalIntroPhase} />
          </Suspense>
        )}
      </GenericErrorBoundary>
    </>
  );
};

export default SeasonalOverlays;
